import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import belowIcon from '../../../asset/icon/below.png';
import { searchMember } from '../../../shared/api/memberApi';
import type { MemberSearchData } from '../../../shared/dto/EntityRepository';
import { getLandingMessage } from '../../../shared/constants/pageMessages';
import { useModalStore } from '../../../shared/store/portal/modalStore';

const LandingSearchModalMobile: React.FC = () => {
  const clearModal = useModalStore((s) => s.clearModal);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberSearchData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const latestSearchId = useRef(0);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const searchId = ++latestSearchId.current;

    if (!normalizedQuery) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }

    setIsSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchMember(normalizedQuery);
        if (latestSearchId.current !== searchId) return;
        setResults(response.data.members ?? []);
      } catch {
        if (latestSearchId.current !== searchId) return;
        setResults([]);
        setSearchFailed(true);
      } finally {
        if (latestSearchId.current === searchId) setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (latestSearchId.current === searchId) latestSearchId.current += 1;
    };
  }, [query]);

  const handleClose = () => {
    clearModal(500);
  };

  const openMemberArchive = (member: MemberSearchData) => {
    if (!member.handle?.trim()) return;
    handleClose();
    navigate(`/@${member.handle}/`);
  };

  return (
    <Container>
      <TitleWrapper>
        <Title>{getLandingMessage('LANDING_SEARCH_TITLE')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </TitleWrapper>
      <SearchInput
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={getLandingMessage('LANDING_SEARCH_PLACEHOLDER')}
        aria-label={getLandingMessage('LANDING_SEARCH_PLACEHOLDER')}
        autoFocus
      />
      <SearchResults>
        {isSearching && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_SEARCHING')}</SearchMessage>
        )}
        {!isSearching && searchFailed && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_FAILED')}</SearchMessage>
        )}
        {!isSearching && !searchFailed && !query.trim() && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_EMPTY')}</SearchMessage>
        )}
        {!isSearching && !searchFailed && query.trim() && results.length === 0 && (
          <SearchMessage>{getLandingMessage('LANDING_SEARCH_NO_RESULTS')}</SearchMessage>
        )}
        {!isSearching &&
          results.map((member) => (
            <SearchResult key={member.aui} type="button" onClick={() => openMemberArchive(member)}>
              <ResultName>{member.name?.trim() || member.username}</ResultName>
              <ResultHandle>@{member.handle}</ResultHandle>
            </SearchResult>
          ))}
      </SearchResults>
    </Container>
  );
};

export default LandingSearchModalMobile;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;


const SearchInput = styled.input`
  width: 100%;
  padding: 6px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  outline: none;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const SearchResults = styled.div`
  min-height: 30vh;
  max-height: 50vh;
  overflow-y: auto;
`;

const SearchResult = styled.button`
  width: 100%;
  padding: 12px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  text-align: left;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

const ResultName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResultHandle = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const SearchMessage = styled.div`
  min-height: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-align: center;
  ${({ theme }) => theme.typography.Body_04};
`;
